/**
 * CustomReportBuilder — pick columns + filters + range, save to lbs_saved_reports.
 *
 * Route: /admin/reports/custom        (new)
 *        /admin/reports/custom?id=…   (open saved)
 *
 * Backed by the same `fetchBookingsForReport` helper; the saved report's
 * JSONB `definition` stores the user choices so the page is fully
 * round-trippable.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Download, RefreshCw, Save } from "lucide-react";
import { listSitesLite } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";
import { useAuth } from "@/lib/auth";
import {
  fetchBookingsForReport,
  resolveRange,
  downloadCsv,
  upsertSavedReport,
  getSavedReport,
} from "@/lib/reportsApi";
import { BOOKING_STATUSES } from "@/lib/constants";
import { toast } from "sonner";
import { REPORT_COLUMNS, TAB_ORDER, tabTitle, cellValue, columnLabel } from "@/lib/reportColumns";
import { useFormVisibility } from "@/lib/formVisibility";
import { AuDateInput } from "@/components/AuDateTime";

const COLUMNS = REPORT_COLUMNS;

const RANGES = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "last_30d", label: "Last 30 days" },
  { value: "last_90d", label: "Last 90 days" },
  { value: "custom", label: "Custom" },
];

const DEFAULT_DEF = {
  columns: ["reference_number","scheduled_start","direction","status","site_name","external_ref","internal_ref"],
  filters: { site_id: null, statuses: [], direction: null },
  range: "this_month",
  custom_from: null,
  custom_to: null,
};

export default function CustomReportBuilder() {
  const [params] = useSearchParams();
  const savedId = params.get("id");
  const { viewAsClientId } = useViewAsClient();
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [def, setDef] = useState(DEFAULT_DEF);

  const [sites, setSites] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState(savedId || null);

  // Load sites
  useEffect(() => {
    let cancelled = false;
    listSitesLite().then(({ data }) => {
      if (!cancelled) setSites(data || []);
    });
    return () => { cancelled = true; };
  }, []);

  // Load saved report if id given
  useEffect(() => {
    if (!savedId) return undefined;
    let cancelled = false;
    (async () => {
      const { data } = await getSavedReport(savedId);
      if (cancelled || !data) return;
      setName(data.name || "");
      setDescription(data.description || "");
      setIsShared(!!data.is_shared);
      setDef({ ...DEFAULT_DEF, ...(data.definition || {}) });
      setCurrentId(data.id);
    })();
    return () => { cancelled = true; };
  }, [savedId]);

  // Run query
  const run = async () => {
    setLoading(true);
    const { from, to } = resolveRange(def.range, def.custom_from, def.custom_to);
    const { data, error } = await fetchBookingsForReport({
      full: true,
      clientId: viewAsClientId || null,
      siteId: def.filters?.site_id || null,
      from, to,
    });
    if (error) toast.error(error.message);
    let filtered = data || [];
    if ((def.filters?.statuses || []).length) {
      filtered = filtered.filter((r) => def.filters.statuses.includes(r.status));
    }
    if (def.filters?.direction) {
      filtered = filtered.filter((r) => r.direction === def.filters.direction);
    }
    setRows(filtered);
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { from, to } = resolveRange(def.range, def.custom_from, def.custom_to);
      const { data, error } = await fetchBookingsForReport({
        full: true,
        clientId: viewAsClientId || null,
        siteId: def.filters?.site_id || null,
        from, to,
      });
      if (cancelled) return;
      if (error) toast.error(error.message);
      let filtered = data || [];
      if ((def.filters?.statuses || []).length) {
        filtered = filtered.filter((r) => def.filters.statuses.includes(r.status));
      }
      if (def.filters?.direction) {
        filtered = filtered.filter((r) => r.direction === def.filters.direction);
      }
      setRows(filtered);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId, def]);

  const vis = useFormVisibility(viewAsClientId || null);
  const selectedCols = useMemo(
    () => COLUMNS.filter((c) => def.columns.includes(c.key)).map((c) => ({ ...c, label: columnLabel(c, vis) })),
    [def.columns, vis]
  );
  const groupedCols = useMemo(() => TAB_ORDER
    .map((t) => ({ tab: t, cols: COLUMNS.filter((c) => c.tab === t) }))
    .filter((g) => g.cols.length), []);

  const exportCsv = () => {
    const csvCols = selectedCols.map((c) => ({
      key: c.key,
      label: c.label,
      format: (_, row) => cellValue(c, row),
    }));
    downloadCsv(`${name || "report"}.csv`, rows, csvCols);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the report a name first"); return; }
    const payload = {
      id: currentId || undefined,
      name: name.trim(),
      description: description.trim() || null,
      definition: def,
      is_shared: isShared,
      client_id: viewAsClientId || null,
      created_by_user_id: user?.id,
    };
    const { data, error } = await upsertSavedReport(payload);
    if (error) { toast.error(error.message); return; }
    setCurrentId(data?.id || currentId);
    toast.success("Saved");
  };

  const toggleCol = (key) => {
    setDef((d) => ({
      ...d,
      columns: d.columns.includes(key)
        ? d.columns.filter((c) => c !== key)
        : [...d.columns, key],
    }));
  };
  const toggleStatus = (s) => {
    setDef((d) => {
      const has = (d.filters.statuses || []).includes(s);
      return {
        ...d,
        filters: {
          ...d.filters,
          statuses: has
            ? d.filters.statuses.filter((x) => x !== s)
            : [...(d.filters.statuses || []), s],
        },
      };
    });
  };

  return (
    <div className="space-y-4" data-testid="custom-report-builder">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/admin/reports" className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Reports
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">Custom report builder</h1>
          <p className="text-sm text-slate-500">
            Pick columns, filters and date range. Save to re-run later or share with your team.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={run} disabled={loading} data-testid="builder-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={exportCsv} disabled={rows.length === 0} data-testid="builder-export-csv">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={save} data-testid="builder-save">
            <Save className="mr-2 h-4 w-4" /> Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Identity */}
        <Card>
          <CardHeader><CardTitle className="text-base">Report identity</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="builder-name" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Description</label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="builder-description" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={isShared} onCheckedChange={setIsShared} data-testid="builder-shared" />
              Share with my team (tenant-wide)
            </label>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader><CardTitle className="text-base">Filters</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Site</label>
                <Select
                  value={def.filters.site_id || "_all_"}
                  onValueChange={(v) => setDef((d) => ({ ...d, filters: { ...d.filters, site_id: v === "_all_" ? null : v } }))}
                >
                  <SelectTrigger data-testid="builder-site"><SelectValue placeholder="All sites" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All sites</SelectItem>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Direction</label>
                <Select
                  value={def.filters.direction || "_all_"}
                  onValueChange={(v) => setDef((d) => ({ ...d, filters: { ...d.filters, direction: v === "_all_" ? null : v } }))}
                >
                  <SelectTrigger data-testid="builder-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">All</SelectItem>
                    <SelectItem value="inbound">Inbound</SelectItem>
                    <SelectItem value="outbound">Outbound</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500">Range</label>
                <Select value={def.range} onValueChange={(v) => setDef((d) => ({ ...d, range: v }))}>
                  <SelectTrigger data-testid="builder-range"><SelectValue /></SelectTrigger>
                  <SelectContent>{RANGES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {def.range === "custom" && (
                <>
                  <div>
                    <label className="text-xs text-slate-500">From</label>
                    <AuDateInput value={def.custom_from || ""} onChange={(e) => setDef((d) => ({ ...d, custom_from: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">To</label>
                    <AuDateInput value={def.custom_to || ""} onChange={(e) => setDef((d) => ({ ...d, custom_to: e.target.value }))} />
                  </div>
                </>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500">Statuses</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(BOOKING_STATUSES || []).map((s) => {
                  const k = typeof s === "string" ? s : s.value;
                  const lbl = typeof s === "string" ? s : s.label;
                  const active = (def.filters.statuses || []).includes(k);
                  return (
                    <button
                      key={k}
                      onClick={() => toggleStatus(k)}
                      data-testid={`builder-status-${k}`}
                      className={`text-xs px-2 py-1 rounded border ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white"}`}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Columns */}
      <Card>
        <CardHeader><CardTitle className="text-base">Columns</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            {groupedCols.map((g) => (
              <div key={g.tab} data-testid={`builder-col-group-${g.tab}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{tabTitle(g.tab, vis)}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {g.cols.map((c) => {
                    const label = columnLabel(c, vis);
                    return (
                      <label key={c.key} className={`flex items-center gap-2 text-sm ${label.includes("switched off") ? "text-slate-400" : ""}`}>
                        <Checkbox
                          checked={def.columns.includes(c.key)}
                          onCheckedChange={() => toggleCol(c.key)}
                          data-testid={`builder-col-${c.key}`}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Preview — {loading ? "loading…" : `${rows.length} rows`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b">
                  {selectedCols.map((c) => <th key={c.key} className="py-2 pr-3 whitespace-nowrap">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={selectedCols.length} className="py-6 text-center text-slate-400 italic">No data</td></tr>
                ) : rows.slice(0, 200).map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                    {selectedCols.map((c) => {
                      return <td key={c.key} className="py-2 pr-3 whitespace-nowrap">{cellValue(c, r)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 200 && (
              <div className="text-xs text-slate-400 mt-2 italic">Showing first 200 rows in preview — full set goes to CSV.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
